// Renderer-side helpers for persisting the user's full name (typed on
// the v3 sign-in screen). Two paths:
//
//   1. Server-side persistence via PATCH /v2/account/openanalyst/me
//      (lands in Product Suit handler that updates the Cognito `name`
//      attribute → flows back through future GET /me as fullName).
//
//   2. Renderer-local fallback in localStorage. Auth context backfills
//      state.user.fullName from here on every setAuthenticated call
//      when the backend still has no name (e.g. before the PATCH
//      response makes it through, or when the user's network blip
//      means PATCH never lands).
//
// Both run in parallel — fast UI update (localStorage) + durable
// persistence (Cognito). If the PATCH fails (backend not deployed,
// network error), the localStorage fallback still works perfectly for
// single-machine users.

import { openAnalystAccountRequest, AuthApiError } from "@/context/auth/api"

const STORAGE_KEY = "openanalyst.onboarding.profile.fullName"

export function saveOnboardingFullName(name: string): void {
  if (typeof localStorage === "undefined") return
  const trimmed = name?.trim()
  if (!trimmed) return
  try {
    localStorage.setItem(STORAGE_KEY, trimmed)
  } catch {
    // storage disabled / full — silently skip, the in-memory injection still works
  }
}

export function loadOnboardingFullName(): string | null {
  if (typeof localStorage === "undefined") return null
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    if (!value) return null
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  } catch {
    return null
  }
}

export function clearOnboardingFullName(): void {
  if (typeof localStorage === "undefined") return
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

export type PushOnboardingFullNameResult = {
  ok: boolean
  // "backend" — landed on Cognito + the next /me will include the name.
  // "local-only" — backend rejected / unreachable; localStorage is the only source.
  // "skipped" — empty input; nothing happened.
  via: "backend" | "local-only" | "skipped"
  error?: string
}

/**
 * Push the typed full name to the OA backend (Cognito `name` attribute)
 * via PATCH /v2/account/openanalyst/me. Best-effort — graceful on 404
 * (endpoint not deployed yet), network errors, etc. The localStorage
 * fallback is the safety net so this is allowed to fail.
 *
 * Returns a structured result so callers (sign-in.tsx) can surface a
 * warning toast if persistence didn't land. Today the call is fire-and-
 * forget; future iterations could retry or queue.
 */
export async function pushOnboardingFullName(fullName: string): Promise<PushOnboardingFullNameResult> {
  const trimmed = fullName?.trim()
  if (!trimmed) return { ok: false, via: "skipped" }
  try {
    await openAnalystAccountRequest("/v2/account/openanalyst/me", {
      method: "PATCH",
      body: { fullName: trimmed },
    })
    return { ok: true, via: "backend" }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    // 404 — backend hasn't deployed the PATCH endpoint yet. Expected
    // during the rollout window before Product Suit deploys this change.
    // 401 — auth context dropped between sign-in completion and this call.
    // 500 — backend error. All cases fall back to localStorage path.
    const status = err instanceof AuthApiError ? err.status : 0
    return {
      ok: false,
      via: "local-only",
      error: status === 404 ? "endpoint_not_deployed" : message,
    }
  }
}
